import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const TooltipOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  pointer-events: none;
`;

const TooltipContainer = styled.div`
  position: absolute;
  background: ${props => props.isDarkMode ? '#0d0d0d' : '#ffffff'};
  border: 1px solid ${props => props.isDarkMode ? '#1a1a1a' : '#e5e7eb'};
  border-radius: 12px;
  padding: 20px;
  max-width: 320px;
  min-width: 280px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  color: ${props => props.isDarkMode ? '#ffffff' : '#111827'};
  font-family: 'Arial', sans-serif;
  pointer-events: auto;
  z-index: 10001;
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    
    ${props => {
      // Don't show arrow for bottom-right position
      if (props.position === 'bottom-right') {
        return 'display: none;';
      }
      
      // Don't show arrow for right-side position
      if (props.position === 'right-side') {
        return 'display: none;';
      }
      
      switch (props.position) {
        case 'top':
          return `
            bottom: -10px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 10px 10px 0 10px;
            border-color: ${props.isDarkMode ? '#0d0d0d' : '#ffffff'} transparent transparent transparent;
          `;
        case 'bottom':
          return `
            top: -10px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 0 10px 10px 10px;
            border-color: transparent transparent ${props.isDarkMode ? '#0d0d0d' : '#ffffff'} transparent;
          `;
        case 'left':
          return `
            right: -10px;
            top: 50%;
            transform: translateY(-50%);
            border-width: 10px 0 10px 10px;
            border-color: transparent transparent transparent ${props.isDarkMode ? '#0d0d0d' : '#ffffff'};
          `;
        case 'right':
          return `
            left: -10px;
            top: 50%;
            transform: translateY(-50%);
            border-width: 10px 10px 10px 0;
            border-color: transparent ${props.isDarkMode ? '#0d0d0d' : '#ffffff'} transparent transparent;
          `;
        default:
          return '';
      }
    }}
  }
`;

const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.isDarkMode ? '#ffffff' : '#111827'};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.isDarkMode ? '#999999' : '#6b7280'};
  cursor: pointer;
  padding: 4px;
  margin-right: 12px;
  
  &:hover {
    color: ${props => props.isDarkMode ? '#ffffff' : '#111827'};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.isDarkMode ? '#999999' : '#6b7280'};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${props => props.isDarkMode ? '#ffffff' : '#111827'};
  }
`;

const TooltipContent = styled.div`
  margin-bottom: 16px;
  line-height: 1.5;
`;

const TooltipDescription = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: ${props => props.isDarkMode ? '#999999' : '#6b7280'};
  font-style: italic;
`;

const TooltipFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const StepIndicator = styled.div`
  font-size: 12px;
  color: ${props => props.isDarkMode ? '#999999' : '#6b7280'};
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #00d67a 0%, #00b368 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.isDarkMode ? '#999999' : '#6b7280'};
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  
  &:hover {
    color: ${props => props.isDarkMode ? '#ffffff' : '#111827'};
  }
`;

const Highlight = styled.div`
  position: absolute;
  border: 3px solid #667eea;
  border-radius: 8px;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
  pointer-events: none;
  z-index: 9999;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(102, 126, 234, 0.2);
    }
    100% {
      box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
    }
  }
`;

const WalkthroughTooltip = ({
  targetSelector,
  title,
  content,
  description,
  stepNumber,
  totalSteps,
  onNext,
  onSkip,
  onClose,
  onBack,
  isDarkMode = false,
  actionText = "Next",
  showSkip = true,
  showBack = true,
  forcePosition = null // New prop to force a specific position
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [tooltipPosition, setTooltipPosition] = useState('bottom');
  const [targetElement, setTargetElement] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const tooltipRef = useRef(null);
  const scrollTimeoutRef = useRef(null);



  useEffect(() => {
    const findTarget = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        setTargetElement(element);
        calculatePosition(element);
        return true;
      }
      return false;
    };

    // Initial attempt to find target
    if (!findTarget()) {
      // If not found initially, keep trying with more frequent retries
      const retryInterval = setInterval(() => {
        if (findTarget()) {
          clearInterval(retryInterval);
        }
      }, 50); // Check every 50ms

      // Clear the interval after 3 seconds to avoid infinite retries
      setTimeout(() => {
        clearInterval(retryInterval);
      }, 3000);
    }

    // Set up mutation observer to watch for DOM changes
    const observer = new MutationObserver(() => {
      // Re-find target element when DOM changes
      setTimeout(() => {
        findTarget();
      }, 10); // Small delay to let DOM settle
    });

    // Observe changes to the entire document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'id']
    });

    // Recalculate position on resize
    const handleResize = () => {
      if (targetElement) {
        calculatePosition(targetElement);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [targetSelector]);

  // Separate effect to recalculate position when targetElement changes
  useEffect(() => {
    if (targetElement) {
      calculatePosition(targetElement);
    }
  }, [targetElement]);

  // Effect to recalculate position when forcePosition changes
  useEffect(() => {
    if (targetElement) {
      calculatePosition(targetElement);
    }
  }, [forcePosition, targetElement]);

  // Effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Set timeout to detect when scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        // Recalculate position after scrolling stops
        if (targetElement) {
          calculatePosition(targetElement);
        }
      }, 150); // 150ms delay after scroll stops
    };

    // Listen for scroll events on window and document
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also listen for scroll on any scrollable parent elements
    const scrollableElements = document.querySelectorAll('[style*="overflow"], .overflow-auto, .overflow-scroll');
    scrollableElements.forEach(el => {
      el.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      scrollableElements.forEach(el => {
        el.removeEventListener('scroll', handleScroll);
      });
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [targetElement]);

  const calculatePosition = (element) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Tooltip dimensions (approximate)
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    
    let x, y, pos;

    // Handle forced positions
    if (forcePosition === 'bottom-right') {
      // Position in bottom right corner
      x = viewportWidth - tooltipWidth - 20;
      y = viewportHeight - tooltipHeight - 20;
      pos = 'bottom-right';
    } else if (forcePosition === 'right-side') {
      // Position at right side of viewport, vertically centered
      x = viewportWidth - tooltipWidth - 20;
      y = Math.max(20, (viewportHeight - tooltipHeight) / 2);
      pos = 'right-side';
    } else if (forcePosition === 'right') {
      // Force position to the right of element
      x = rect.right + 12;
      y = rect.top + rect.height / 2;
      pos = 'right';
    } else if (forcePosition === 'left') {
      // Force position to the left of element
      x = rect.left - 12;
      y = rect.top + rect.height / 2;
      pos = 'left';
    } else if (forcePosition === 'top') {
      // Force position above the element
      x = rect.left + rect.width / 2;
      y = rect.top - 12;
      pos = 'top';
    } else {
      // Default automatic positioning
      x = rect.left + rect.width / 2;
      y = rect.bottom + 12;
      pos = 'bottom';

      // Check if tooltip would go off bottom of screen
      if (y + tooltipHeight > viewportHeight) {
        // Try positioning above
        const topY = rect.top - tooltipHeight - 12;
        if (topY >= 0) {
          y = topY;
          pos = 'top';
        } else {
          // If both top and bottom don't work, use side positioning
          if (rect.left > tooltipWidth + 12) {
            // Position to the left
            x = rect.left - 12;
            y = Math.max(12, Math.min(rect.top + rect.height / 2, viewportHeight - tooltipHeight - 12));
            pos = 'left';
          } else if (rect.right + tooltipWidth + 12 < viewportWidth) {
            // Position to the right
            x = rect.right + 12;
            y = Math.max(12, Math.min(rect.top + rect.height / 2, viewportHeight - tooltipHeight - 12));
            pos = 'right';
          } else {
            // Last resort: center in viewport
            x = viewportWidth / 2;
            y = Math.max(12, Math.min(viewportHeight / 2, viewportHeight - tooltipHeight - 12));
            pos = 'bottom';
          }
        }
      }

      // Check if tooltip would go off right side of screen (for bottom/top positioning)
      if ((pos === 'bottom' || pos === 'top') && x + tooltipWidth / 2 > viewportWidth) {
        x = viewportWidth - tooltipWidth / 2 - 12;
      } else if ((pos === 'bottom' || pos === 'top') && x - tooltipWidth / 2 < 0) {
        x = tooltipWidth / 2 + 12;
      }
    }

    // Ensure tooltip is never positioned off-screen (unless forced position)
    if (forcePosition !== 'bottom-right') {
      x = Math.max(tooltipWidth / 2 + 12, Math.min(x, viewportWidth - tooltipWidth / 2 - 12));
      y = Math.max(12, Math.min(y, viewportHeight - tooltipHeight - 12));
    }

    setPosition({ x, y });
    setTooltipPosition(pos);
  };

  const getTooltipStyle = () => {
    const baseStyle = {
      left: position.x,
      top: position.y,
    };

    switch (tooltipPosition) {
      case 'top':
        return {
          ...baseStyle,
          transform: 'translate(-50%, -100%)',
        };
      case 'bottom':
        return {
          ...baseStyle,
          transform: 'translate(-50%, 0)',
        };
      case 'left':
        return {
          ...baseStyle,
          transform: 'translate(-100%, -50%)',
        };
      case 'right':
        return {
          ...baseStyle,
          transform: 'translate(0, -50%)',
        };
      case 'bottom-right':
        return {
          ...baseStyle,
          transform: 'translate(0, 0)',
        };
      case 'right-side':
        return {
          ...baseStyle,
          transform: 'translate(0, 0)',
        };
      default:
        return baseStyle;
    }
  };

  const getHighlightStyle = () => {
    if (!targetElement) return {};
    
    const rect = targetElement.getBoundingClientRect();
    return {
      left: rect.left - 4,
      top: rect.top - 4,
      width: rect.width + 8,
      height: rect.height + 8,
    };
  };

  if (!targetElement) return null;

  return (
    <TooltipOverlay>
      {!isScrolling && <Highlight style={getHighlightStyle()} />}
      <TooltipContainer
        ref={tooltipRef}
        style={getTooltipStyle()}
        position={tooltipPosition}
        isDarkMode={isDarkMode}
      >
        <TooltipHeader>
          <HeaderButtons>
            {showBack && stepNumber > 1 && (
              <BackButton onClick={onBack} isDarkMode={isDarkMode}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </BackButton>
            )}
            <TooltipTitle isDarkMode={isDarkMode}>{title}</TooltipTitle>
          </HeaderButtons>
          <CloseButton onClick={onClose} isDarkMode={isDarkMode}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </TooltipHeader>
        
        <TooltipContent>
          {content}
          {description && (
            <TooltipDescription isDarkMode={isDarkMode}>
              {description}
            </TooltipDescription>
          )}
        </TooltipContent>
        
        <TooltipFooter>
          <StepIndicator isDarkMode={isDarkMode}>
            Step {stepNumber} of {totalSteps}
          </StepIndicator>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {showSkip && (
              <SkipButton onClick={onSkip} isDarkMode={isDarkMode}>
                Skip tutorial
              </SkipButton>
            )}
            <ActionButton onClick={onNext}>
              {actionText}
              <FontAwesomeIcon icon={faArrowRight} size="sm" />
            </ActionButton>
          </div>
        </TooltipFooter>
      </TooltipContainer>
    </TooltipOverlay>
  );
};

export default WalkthroughTooltip; 